// src/features/patient/care-treatment/components/careParts.tsx

/**
 * Purpose : Small pieces the visit and note screens share.
 *
 * Contents :
 *
 * * function(SourceBadges) : "Epic" "PDF" chips for a record
 * * function(sourcesAsGolden) : a merged record's sources in the golden-record list's shape
 * * function(WhereFrom) : "Where this came from" under a visit's or note's heading (as on Medications)
 * * function(KindTile) : the visit-type icon tile
 * * function(ClampedText) : first lines of record text with Show more / Show less
 * * function(LinkedChips) : items that open their own section (Diagnoses, Medications, Diagnostics)
 * * function(DocumentRows) : forms / other parts of a file, each with View original
 * * function(EmptyCardNote) : the one line a genuinely empty card shows instead of nothing
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import type { LinkedItem, OtherDocument, Source, VisitKind } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { SOURCE_KIND_LABEL, pageRangeText } from "@/features/patient/care-treatment/utils/careTimeline";
import { kindMeta } from "@/features/patient/care-treatment/utils/careNaming";
import { GoldenSources, type GoldenSourceExtra } from "@/features/empi/GoldenRecord";
import type { EmpiRecordGolden } from "@/shared/types/empi";
import {
  bodyTextClass, greyChipClass, navyChipClass, navyLinkChipClass, outlineButtonClass,
  textLinkClass,
} from "./careStyles";

// function(SourceBadges)
/**
 * One chip per kind of source ("Epic", "PDF"), each kind once.
 *
 * @param sources - Where the record came from.
 */
export function SourceBadges({ sources }: { sources: Source[] }) {
  const kinds = [...new Set(sources.map((s) => s.kind))];
  if (kinds.length === 0) return null;

  return (
    <span className="flex flex-wrap gap-1.5" aria-label="Sources">
      {kinds.map((kind) => (
        <span key={kind} className={navyChipClass}>{SOURCE_KIND_LABEL[kind]}</span>
      ))}
    </span>
  );
}

// function(sourcesAsGolden)
/**
 * A merged record's sources in the shape of the platform's golden record
 * (GoldenSources): the first is the copy shown; each source's pages and its
 * View original go in the source's own line.
 */
export function sourcesAsGolden(sources: Source[], onViewOriginal?: (source: Source) => void): {
  golden: EmpiRecordGolden;
  extras: Record<string, GoldenSourceExtra>;
} {
  const extras: Record<string, GoldenSourceExtra> = {};
  const goldenSources = sources.map((source, i) => {
    const recordId = `source-${i}`;
    const pages = pageRangeText(source);
    extras[recordId] = {
      detail: pages || undefined,
      action: onViewOriginal && source.originalRef ? { label: "View original", onClick: () => onViewOriginal(source) } : undefined,
    };
    return {
      recordId,
      sourceVendor: SOURCE_KIND_LABEL[source.kind],
      sourceTier: 2,
      structured: source.kind !== "pdf",
      uploadId: source.uploadId ?? null,
      sourceLabel: source.label || null,
      isGolden: i === 0,
    };
  });
  return {
    golden: {
      clusterId: "", goldenConfidence: sources.some((s) => s.kind !== "pdf") ? "confirmed" : "unconfirmed_document",
      corroboratingSourceCount: sources.length, sources: goldenSources, fieldSources: {}, resolvedAt: null,
    },
    extras,
  };
}

// function(WhereFrom)
/**
 * "Where this came from (N sources)": the small link Medications shows under
 * a medicine's name, here at the end of a visit's or note's heading. Opened,
 * each source's file, pages and View original.
 *
 * @param sources - Where the record came from; the first is the copy shown.
 * @param onViewOriginal - Open a source's original pages.
 */
export function WhereFrom({ sources, onViewOriginal, className }: {
  sources: Source[];
  onViewOriginal?: (source: Source) => void;
  className?: string;
}) {
  if (sources.length === 0) return null;
  const { golden, extras } = sourcesAsGolden(sources, onViewOriginal);
  return (
    <GoldenSources
      golden={golden}
      sourceExtras={extras}
      compact
      className={cn("[&>button]:items-start [&>button]:text-left", className)}
    />
  );
}

// function(KindTile)
/** The visit-type icon on its tinted tile. */
export function KindTile({ kind, size = "md" }: { kind: VisitKind | undefined; size?: "sm" | "md" }) {
  const meta = kindMeta(kind);
  return (
    <span
      aria-hidden="true"
      data-kind={kind ?? "other"}
      className={cn("flex shrink-0 items-center justify-center", meta.tile, size === "sm" ? "h-9 w-9 rounded-xl" : "h-10 w-10 rounded-xl")}
    >
      <Icon icon={meta.icon} className={size === "sm" ? "h-[18px] w-[18px]" : "h-5 w-5"} />
    </span>
  );
}

// function(ClampedText)
/**
 * Record text as printed, its first `lines` lines with Show more / Show less
 * when there is more.
 *
 * @param text - The text, verbatim.
 * @param lines - Lines shown while closed.
 */
export function ClampedText({ text, lines = 3 }: { text: string; lines?: number }) {
  const [open, setOpen] = useState(false);
  const all = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const long = all.length > lines || text.length > lines * 90;
  if (all.length === 0) return null;
  return (
    <div>
      <p className={cn("!mb-0 whitespace-pre-wrap break-words leading-relaxed", bodyTextClass, long && !open && clampClass[lines])}>
        {open || !long ? all.join("\n") : all.slice(0, lines).join("\n")}
      </p>
      {long && (
        <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cn("mt-1", textLinkClass)}>
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

const clampClass: Record<number, string> = { 3: "line-clamp-3", 5: "line-clamp-5" };

/** GlobalSearch's highlight. */
export const markClass = "rounded-sm bg-[#FEF6DC] p-0 text-inherit";

// function(LinkedChips)
/**
 * Items recorded at a visit; each opens the section that holds the full record.
 *
 * @param items - The diagnoses, medicines, tests or vitals.
 * @param to - That section's page; omitted, the items are plain chips.
 */
export function LinkedChips({ items, to }: { items: LinkedItem[]; to?: string }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) =>
        to ? (
          <button key={item.id} type="button" onClick={() => navigate(to)} className={navyLinkChipClass}>
            {item.label}
            <Icon icon="lucide:arrow-right" className="h-3 w-3" />
          </button>
        ) : (
          <span key={item.id} className={greyChipClass}>{item.label}</span>
        ),
      )}
    </div>
  );
}

// function(DocumentRows)
/**
 * Parts of an uploaded file that aren't notes (forms, flowsheets, unclassified
 * pages, scans waiting for text): title, file and pages, and View original.
 *
 * @param documents - The documents, in file order.
 * @param onViewOriginal - Open one document's original pages.
 */
export function DocumentRows({ documents, onViewOriginal }: {
  documents: OtherDocument[];
  onViewOriginal: (source: Source) => void;
}) {
  return (
    <div className="grid gap-2">
      {documents.map((doc) => {
        const detail = [doc.source.label, pageRangeText(doc.source), doc.kind === "pending" && "Text coming"]
          .filter(Boolean)
          .join(" · ");
        return (
          <div key={doc.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3">
            <span className="flex min-w-0 items-center gap-3">
              <Icon icon={doc.kind === "form" ? "lucide:clipboard-list" : "lucide:file"} className="h-5 w-5 shrink-0 text-[#0A2947]" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-extrabold text-[#0A2947]">{doc.title}</span>
                {detail && <span className="block truncate text-xs text-[#6B7280]">{detail}</span>}
              </span>
            </span>
            <Button variant="outline" onClick={() => onViewOriginal(doc.source)} className={outlineButtonClass}>
              View original
            </Button>
          </div>
        );
      })}
    </div>
  );
}

// function(EmptyCardNote)
/** A card with nothing in it but its heading: says so plainly (an info icon and one muted line). */
export function EmptyCardNote({ children, className }: { children: string; className?: string }) {
  return (
    <p className={cn("!mb-0 flex items-start gap-2 text-sm text-[#6B7280]", className)}>
      <Icon icon="lucide:info" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}
